import { useEffect, useState, useCallback } from "react";
import { Card, CardContent, Typography, Box, CircularProgress, Grid, Alert } from "@mui/material";
import { Error as ErrorIcon } from "@mui/icons-material";
import { GetDataFromApiWithParams } from "../../../../services/api/ExecuteApiRequests";

// Helper function to convert any value to number
const toNumber = (value) => {
  if (typeof value === 'number' && !isNaN(value)) return value;
  if (typeof value === 'string') {
    const num = parseFloat(value);
    return isNaN(num) ? 0 : num;
  }
  if (typeof value === 'object' && value !== null) {
    const num = parseFloat(value.toString());
    return isNaN(num) ? 0 : num;
  }
  return 0;
};

// Format number with commas
const formatNumber = (number) => {
  const num = toNumber(number);
  if (num === 0) return "0";
  return new Intl.NumberFormat('en-US').format(num);
};

// Individual Park Pie Chart Component
const ParkPieChart = ({ parkId, parkName, data }) => {
  const total = Object.values(data).reduce((sum, val) => sum + toNumber(val), 0);
  const colors = ['#1976d2', '#4caf50', '#795548'];
  
  if (total === 0) {
    return (
      <Card sx={{ bgcolor: '#f5f5f5', p: 2, height: '100%' }}>
        <Typography variant="body2" color="textSecondary" textAlign="center">
          No land use data available for {parkName}
        </Typography>
      </Card>
    );
  }
  
  let startAngle = 0;
  const centerX = 100, centerY = 100, radius = 80;
  const svgSize = 220;
  
  const items = Object.keys(data).filter(key => toNumber(data[key]) > 0);
  
  return (
    <Card sx={{ bgcolor: '#f5f5f5', p: 2, height: '100%' }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#1976d2', textAlign: 'center', mb: 1 }}>
        {parkName}
      </Typography>
      <Typography variant="caption" color="textSecondary" sx={{ textAlign: 'center', display: 'block', mb: 1 }}>
        ID: {parkId}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#795548', textAlign: 'center', mb: 2 }}>
        Total: {formatNumber(total)} m² ({(total / 10000).toFixed(2)} ha)
      </Typography>
      
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        {/* Pie Chart SVG */}
        <Box sx={{ position: 'relative', width: svgSize, height: svgSize }}>
          <svg width={svgSize} height={svgSize} viewBox={`0 0 ${svgSize} ${svgSize}`}>
            {items.map((key, index) => {
              const value = toNumber(data[key]);
              const percentage = (value / total) * 100;
              const angle = (percentage / 100) * 360;
              const endAngle = startAngle + angle;
              
              const startRad = (startAngle * Math.PI) / 180;
              const endRad = (endAngle * Math.PI) / 180;
              
              const x1 = centerX + radius * Math.cos(startRad);
              const y1 = centerY + radius * Math.sin(startRad);
              const x2 = centerX + radius * Math.cos(endRad);
              const y2 = centerY + radius * Math.sin(endRad);
              
              const largeArcFlag = angle > 180 ? 1 : 0;
              const pathData = `M ${centerX} ${centerY} L ${x1} ${y1} A ${radius} ${radius} 0 ${largeArcFlag} 1 ${x2} ${y2} Z`;
              
              const slice = (
                <path 
                  key={key} 
                  d={pathData} 
                  fill={colors[index % colors.length]} 
                  stroke="white" 
                  strokeWidth="2"
                />
              );
              startAngle = endAngle;
              return slice;
            })}
            <circle cx={centerX} cy={centerY} r="30" fill="white" />
            <text x={centerX} y={centerY - 3} textAnchor="middle" fontSize="9" fontWeight="bold" fill="#333">Total</text>
            <text x={centerX} y={centerY + 8} textAnchor="middle" fontSize="8" fontWeight="bold" fill="#1976d2">
              {(total / 10000).toFixed(1)}ha
            </text>
          </svg>
        </Box>
        
        {/* Legend */}
        <Box sx={{ minWidth: 120 }}>
          {items.map((key, index) => {
            const value = toNumber(data[key]);
            const percentage = (value / total) * 100;
            return (
              <Box key={key} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Box sx={{ width: 12, height: 12, bgcolor: colors[index % colors.length], borderRadius: 1 }} />
                <Typography variant="caption" sx={{ fontSize: '0.7rem', flex: 1 }}>
                  {key}
                </Typography>
                <Typography variant="caption" fontWeight="bold" sx={{ fontSize: '0.65rem' }}>
                  {percentage.toFixed(1)}%
                </Typography>
              </Box>
            );
          })}
        </Box>
      </Box>
    </Card>
  );
};

// Main component
const ParkLandUsePage = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [parksData, setParksData] = useState([]);
  const [parcelsData, setParcelsData] = useState([]);
  const [greenAreasData, setGreenAreasData] = useState([]);
  const [roadsData, setRoadsData] = useState([]);
  const [parkLandUseData, setParkLandUseData] = useState({});

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Fetch all data
      const [parks, parcels, greenAreas, roads] = await Promise.all([
        GetDataFromApiWithParams(`parks/parksummary`),
        GetDataFromApiWithParams(`parks/parcelsummary`),
        GetDataFromApiWithParams(`parks/greenareasummary`),
        GetDataFromApiWithParams(`parks/roadsummary`)
      ]);
      
      console.log("Fetched data:", {
        parksCount: parks?.length,
        parcelsCount: parcels?.length,
        greenAreasCount: greenAreas?.length,
        roadsCount: roads?.length
      });
      
      setParksData(parks || []);
      setParcelsData(parcels || []);
      setGreenAreasData(greenAreas || []);
      setRoadsData(roads || []);
      
    } catch (error) {
      console.error("Failed to fetch data:", error);
      setError(error.message || "Failed to load data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Calculate land use for each park
  useEffect(() => {
    if (parksData.length > 0 && parcelsData.length > 0) {
      const landUseByPark = {};
      
      parksData.forEach(park => {
        const parkId = park.id;
        const parkName = park.name;
        
        // Filter parcels for this park
        const parkParcels = parcelsData.filter(p => toNumber(p.park_id) === toNumber(parkId));
        const totalParcelArea = parkParcels.reduce((sum, p) => sum + toNumber(p.area), 0);
        
        // Filter green areas for this park
        const parkGreenAreas = greenAreasData.filter(g => toNumber(g.park_id) === toNumber(parkId));
        const totalGreenArea = parkGreenAreas.reduce((sum, g) => sum + toNumber(g.area || g.capacity || 0), 0);
        
        // Filter roads for this park
        const parkRoads = roadsData.filter(r => toNumber(r.park_id) === toNumber(parkId));
        const totalRoadArea = parkRoads.reduce((sum, r) => sum + toNumber(r.area || r.capacity || 0), 0);
        
        landUseByPark[parkId] = {
          parkId: parkId,
          parkName: parkName,
          landUse: {
            'Parcels': totalParcelArea,
            'Green Areas': totalGreenArea,
            'Roads': totalRoadArea
          }
        };
      });
      
      console.log("Calculated land use by park:", landUseByPark);
      setParkLandUseData(landUseByPark);
    }
  }, [parksData, parcelsData, greenAreasData, roadsData]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', minHeight: 400, gap: 2 }}>
        <CircularProgress />
        <Typography variant="body2" color="textSecondary">
          Loading park land use data...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', minHeight: 400, gap: 2, p: 3 }}>
        <ErrorIcon sx={{ fontSize: 60, color: '#f44336' }} />
        <Alert severity="error" sx={{ width: '100%', maxWidth: 600 }}>
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#1976d2', mb: 2, textAlign: 'center' }}>
        Park Land Use Distribution
      </Typography>
      <Typography variant="body1" color="textSecondary" sx={{ textAlign: 'center', mb: 4 }}>
        Land use breakdown by park (Parcels, Green Areas, and Roads)
      </Typography>
      
      <Grid container spacing={3}>
        {Object.values(parkLandUseData).map((park) => (
          <Grid item xs={12} sm={6} md={4} key={park.parkId}>
            <ParkPieChart 
              parkId={park.parkId}
              parkName={park.parkName}
              data={park.landUse}
            />
          </Grid>
        ))}
      </Grid>
      
      {Object.keys(parkLandUseData).length === 0 && (
        <Box sx={{ textAlign: 'center', py: 5 }}>
          <Typography variant="body1" color="textSecondary">
            No park land use data available
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default ParkLandUsePage;